import { AnimalSplitType } from '@farmshare/utils';
import { useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
  Cutsheet,
  ProducerJobByIdDocument,
  useCutsheetCapabilityManyQuery,
  useCutsheetQuestionnaireManyQuery,
  useProcessorSchedulingBuyerCutsheetReminderMutation,
  useProcessorSchedulingCutsheetsPartialUpdateMutation,
  useProcessorSchedulingSplitTypeUpdateByIdMutation,
  User,
} from 'lib/graphql';
import { isProcessor } from 'lib/userHelpers';
import { userState, vendorState } from 'state';

import { useDownloadCutsheetPdf } from '../../../shared/hooks/use-download-cutsheet-pdf';

import {
  AnimalHead,
  CutsheetInfo,
  Job,
} from '../../../shared/split-slot/types';
import {
  getQuestionnaireId,
  getSplitSlots,
} from '../../../shared/split-slot/utils';
import { matchSlotsToContacts } from '../../../utils/match-slots-to-contacts';

import {
  getReminderSentTo as _getReminderSentTo,
  slotCanSendReminder as _slotCanSendReminder,
  type ReminderContext,
} from './reminder-helpers';
import { useDrawerStates } from './use-drawer-states';

const UNASSIGN_SLOT = '__UNASSIGN__';

export function useHeadSplitSection(
  job: Job,
  head: AnimalHead,
  hasCapabilitiesV2 = false,
) {
  const user = useRecoilValue(userState);
  const vendor = useRecoilValue(vendorState);

  const refetchJob = {
    query: ProducerJobByIdDocument,
    variables: { id: job._id },
  };

  const [updateSplitType, { loading: savingSplitType }] =
    useProcessorSchedulingSplitTypeUpdateByIdMutation({
      refetchQueries: [refetchJob],
    });

  const { generatingPdf, downloadPdf } = useDownloadCutsheetPdf();

  const [sendBuyerReminder, { loading: sendingReminder }] =
    useProcessorSchedulingBuyerCutsheetReminderMutation();

  // --- Reminder helpers (pure functions with stable context) ----------------

  const vendorId = job.vendor?._id;
  const producerId = job.requestedBy?._id;
  const processorHasCutsheetNeeded = job.hasCutsheetNeededNotification === true;

  // Questionnaire support — only active when CAPABILITIES_V2 module is enabled
  // Step 1: load the active capability for this vendor + species
  const { data: capabilityData } = useCutsheetCapabilityManyQuery({
    variables: {
      vendorId,
      species: job.animalSpecies as string,
      isActive: true,
    },
    skip: !hasCapabilitiesV2 || !vendorId,
  });

  const capabilityId =
    capabilityData?.CutsheetCapabilityByVendorAndAnimal?.[0]?._id ?? null;

  // Step 2: load published questionnaires for that capability
  const { data: questionnairesData } = useCutsheetQuestionnaireManyQuery({
    variables: { capabilityId: capabilityId!, status: 'published' },
    skip: !capabilityId,
  });

  const questionnaireOptions = useMemo(
    () =>
      questionnairesData?.cutsheetQuestionnairesByVendorAndAnimal?.map((q) => ({
        label: q!.name,
        value: q!._id as string,
      })) ?? [],
    [questionnairesData],
  );

  const reminderCtx = useMemo<ReminderContext>(
    () => ({ processorHasCutsheetNeeded, vendorId, producerId }),
    [processorHasCutsheetNeeded, vendorId, producerId],
  );

  const slotCanSendReminder = useCallback(
    (slotInfo: CutsheetInfo | null): boolean =>
      _slotCanSendReminder(slotInfo, reminderCtx),
    [reminderCtx],
  );

  const getReminderSentTo = useCallback(
    (slotInfo: CutsheetInfo | null): string | null =>
      _getReminderSentTo(slotInfo, job.notificationHistory),
    [job.notificationHistory],
  );

  // --- Split type & slot state ----------------------------------------------

  const availableSplitTypes = useMemo(() => {
    const setting = job.processorSettings?.animalSettings?.find(
      (s) => (s?.species as string) === (job.animalSpecies as string),
    );
    return (setting?.splitTypes?.filter(Boolean) as string[]) ?? [];
  }, [job]);

  const existingSplit = head?.splitType as AnimalSplitType | null | undefined;
  const [selectedSplit, setSelectedSplit] = useState<AnimalSplitType | null>(
    existingSplit ?? null,
  );

  const slots = useMemo(
    () => (selectedSplit ? getSplitSlots(selectedSplit) : []),
    [selectedSplit],
  );

  const slotContacts = useMemo(
    () =>
      matchSlotsToContacts(slots, head?.cutsheetInformation ?? []).map(
        (r) => r?.match ?? null,
      ),
    [head?.cutsheetInformation, slots],
  );

  // Returns the current questionnaire ID for a given slot index
  const getSlotQuestionnaireId = useCallback(
    (slotIndex: number): string | null =>
      getQuestionnaireId(slotContacts[slotIndex]?.questionnaire),
    [slotContacts],
  );

  // --- Drawer state ---------------------------------------------------------

  const {
    drawerSlotIndex,
    setDrawerSlotIndex,
    newBuyerSlotIndex,
    setNewBuyerSlotIndex,
    cutsheetDrawerSlotIndex,
    setCutsheetDrawerSlotIndex,
    wizardSlotIndex,
    setWizardSlotIndex,
    cutsheetDrawerInfo,
    questionnaireResponseDrawerSlotIndex,
    setQuestionnaireResponseDrawerSlotIndex,
    questionnaireResponseDrawerInfo,
    questionnaireEditWizardSlotIndex,
    setQuestionnaireEditWizardSlotIndex,
    questionnaireEditWizardInfo,
  } = useDrawerStates(slotContacts);

  // --- Cutsheet assignment --------------------------------------------------

  const [assignCutsheetToSlot] =
    useProcessorSchedulingCutsheetsPartialUpdateMutation({
      refetchQueries: [refetchJob],
    });

  const [togglingLockSlotId, setTogglingLockSlotId] = useState<string | null>(
    null,
  );

  const handleToggleSlotLock = useCallback(
    async (slotIndex: number) => {
      const info = slotContacts[slotIndex];
      if (!info?._id) return;
      setTogglingLockSlotId(info._id);
      try {
        await assignCutsheetToSlot({
          variables: {
            processorSchedulingId: job._id,
            record: [{ _id: info._id, locked: !info.locked }],
          },
        });
      } finally {
        setTogglingLockSlotId(null);
      }
    },
    [slotContacts, assignCutsheetToSlot, job._id],
  );

  const handleWizardCutsheetCreated = useCallback(
    async (cutsheet: Cutsheet) => {
      if (wizardSlotIndex == null) return;
      const info = slotContacts[wizardSlotIndex];
      if (!info?._id) return;

      await assignCutsheetToSlot({
        variables: {
          processorSchedulingId: job._id,
          record: [{ _id: info._id, cutsheet: cutsheet._id }],
        },
      });
      setWizardSlotIndex(null);
    },
    [
      wizardSlotIndex,
      slotContacts,
      assignCutsheetToSlot,
      job._id,
      setWizardSlotIndex,
    ],
  );

  // --- Buyer assignment helpers ---------------------------------------------

  const buildCutsheetPayload = useCallback(
    (overrideIndex: number, overrideUserId: string) => {
      if (!selectedSplit) return null;

      // Always include every slot at its original index so the backend can
      // line up `existingCutsheetInformation[idx]` correctly. Slots without a
      // contactUser are treated as unassigned placeholders server-side.
      return slots.map((s, i) => {
        if (i === overrideIndex) {
          if (overrideUserId === UNASSIGN_SLOT) {
            return { splitPart: s.part };
          }
          return { splitPart: s.part, contactUser: overrideUserId };
        }
        const existing = slotContacts[i];
        if (existing?.contactUser?._id) {
          return { splitPart: s.part, contactUser: existing.contactUser._id };
        }
        return { splitPart: s.part };
      });
    },
    [selectedSplit, slots, slotContacts],
  );

  const assignBuyerToSlot = useCallback(
    (slotIndex: number, userId: string) => {
      const cutsheetInformation = buildCutsheetPayload(slotIndex, userId);
      if (!cutsheetInformation) return;

      updateSplitType({
        variables: {
          processorSchedulingId: job._id,
          record: {
            animalHeadId: head!._id,
            splitType: selectedSplit,
            cutsheetInformation,
          },
        },
      });
    },
    [buildCutsheetPayload, updateSplitType, job._id, head, selectedSplit],
  );

  // --- Event handlers -------------------------------------------------------

  const handleSelectSplitType = useCallback(
    (opt: string) => {
      const splitType = opt as AnimalSplitType;
      setSelectedSplit(splitType);
      updateSplitType({
        variables: {
          processorSchedulingId: job._id,
          record: {
            animalHeadId: head!._id,
            splitType: opt,
          },
        },
      });
    },
    [updateSplitType, job._id, head],
  );

  const handleSelectMyself = useCallback(
    (slotIndex: number) => {
      const targetUserId =
        user && isProcessor(user) ? job.requestedBy?._id : user?._id;
      if (targetUserId) assignBuyerToSlot(slotIndex, targetUserId);
    },
    [user, job.requestedBy?._id, assignBuyerToSlot],
  );

  const handleLeaveUnassigned = useCallback(
    (slotIndex: number) => assignBuyerToSlot(slotIndex, UNASSIGN_SLOT),
    [assignBuyerToSlot],
  );

  const handleSelectExisting = useCallback(
    (userId: string) => {
      if (drawerSlotIndex != null) assignBuyerToSlot(drawerSlotIndex, userId);
    },
    [drawerSlotIndex, assignBuyerToSlot],
  );

  const handleNewBuyerCreated = useCallback(
    (created: User) => {
      if (newBuyerSlotIndex != null) {
        assignBuyerToSlot(newBuyerSlotIndex, created._id);
      }
      setNewBuyerSlotIndex(null);
    },
    [newBuyerSlotIndex, assignBuyerToSlot, setNewBuyerSlotIndex],
  );

  const handleSelectQuestionnaire = useCallback(
    async (slotIndex: number, questionnaireId: string | null) => {
      const info = slotContacts[slotIndex];
      if (!info?._id) return;
      await assignCutsheetToSlot({
        variables: {
          processorSchedulingId: job._id,
          record: [{ _id: info._id, questionnaire: questionnaireId }],
        },
      });
    },
    [slotContacts, assignCutsheetToSlot, job._id],
  );

  const handleDownloadPdf = useCallback(
    async (slotIndex: number) => {
      const info = slotContacts[slotIndex];
      if (!info?._id) return;
      await downloadPdf(job._id, info._id);
    },
    [slotContacts, downloadPdf, job._id],
  );

  const handleSendReminder = useCallback(
    async (slotIndex: number) => {
      const slotInfo = slotContacts[slotIndex];
      const buyerUserId = slotInfo?.contactUser?._id;
      if (!buyerUserId) return;

      await sendBuyerReminder({
        variables: {
          schedulingId: job._id,
          contactUserId: buyerUserId,
        },
        refetchQueries: [refetchJob],
      });
    },
    [slotContacts, sendBuyerReminder, job._id, refetchJob],
  );

  return {
    user,
    vendor,
    selectedSplit,
    availableSplitTypes,
    slots,
    slotContacts,
    savingSplitType,
    generatingPdf,
    drawerSlotIndex,
    setDrawerSlotIndex,
    newBuyerSlotIndex,
    setNewBuyerSlotIndex,
    cutsheetDrawerSlotIndex,
    setCutsheetDrawerSlotIndex,
    cutsheetDrawerInfo,
    wizardSlotIndex,
    setWizardSlotIndex,
    handleWizardCutsheetCreated,
    handleSelectSplitType,
    handleSelectMyself,
    handleLeaveUnassigned,
    handleSelectExisting,
    handleNewBuyerCreated,
    handleDownloadPdf,
    handleSendReminder,
    sendingReminder,
    slotCanSendReminder,
    getReminderSentTo,
    questionnaireOptions,
    getSlotQuestionnaireId,
    handleSelectQuestionnaire,
    questionnaireResponseDrawerSlotIndex,
    setQuestionnaireResponseDrawerSlotIndex,
    questionnaireResponseDrawerInfo,
    questionnaireEditWizardSlotIndex,
    setQuestionnaireEditWizardSlotIndex,
    questionnaireEditWizardInfo,
    handleToggleSlotLock,
    togglingLockSlotId,
  };
}
